import { Fragment, useEffect, useState } from 'react';
import type { FreezeFrame, ScanResult } from '../lib/elm327.ts';
import { allDtcs } from '../lib/elm327.ts';
import type { Dtc } from '../lib/obd/dtc.ts';
import { PIDS, convertVital, formatVital, type UnitSystem, type Vitals } from '../lib/obd/pids.ts';
import { formatVin } from '../lib/obd/vin.ts';
import { api, type Vehicle } from '../lib/api.ts';
import { CalibrationPanel, MonitorTestsPanel, ReadinessPanel } from './DiagnosticsPanels.tsx';
import { VehicleExtras, VehicleSpecs } from './VehicleCard.tsx';
import { RecallsPanel } from './RecallsPanel.tsx';
import { LivePanel } from './LivePanel.tsx';
import { SummaryPanel } from './SummaryPanel.tsx';
import { Term } from './Term.tsx';

interface Props {
  result: ScanResult | null;
  selectedCode: string | null;
  onSelectCode: (code: string | null) => void;
  savedScanId: number | null;
  live: boolean;
  liveKeys: string[];
  samples: Record<string, number[]>;
  liveHz: number | null;
  onToggleLiveKey: (key: string) => void;
  units: UnitSystem;
}

export function ScanView({
  result,
  selectedCode,
  onSelectCode,
  savedScanId,
  live,
  liveKeys,
  samples,
  liveHz,
  onToggleLiveKey,
  units,
}: Props) {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [label, setLabel] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);

  useEffect(() => {
    if (!result?.vin) {
      setVehicle(null);
      return;
    }
    let cancelled = false;
    api
      .getVehicle(result.vin)
      .then((v) => {
        if (cancelled) return;
        setVehicle(v);
        setLabel(v.label ?? '');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [result?.vin, savedScanId]);

  if (!result) {
    return (
      <div className="empty">
        <strong>No scan yet</strong>
        Pick an adapter and press Scan vehicle. Turn the ignition to ON first — the engine does not need to be running,
        but the modules do need power.
      </div>
    );
  }

  const codes = allDtcs(result);
  const stored = codes.filter((c) => c.kind === 'stored');

  async function saveLabel() {
    if (!result?.vin) return;
    setSavingLabel(true);
    try {
      const updated = await api.labelVehicle(result.vin, label);
      setVehicle(updated);
    } finally {
      setSavingLabel(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <SummaryPanel result={result} vehicle={vehicle} scanId={savedScanId} />

      <div className="card">
        <div className="vehicle">
          <div style={{ minWidth: 0 }}>
            <span className="eyebrow">Vehicle</span>
            <p className="title">
              {vehicle?.label ||
                [vehicle?.modelYear, vehicle?.make, vehicle?.model].filter(Boolean).join(' ') ||
                'Unidentified vehicle'}
            </p>
            <p className="vin">{result.vin ? formatVin(result.vin) : 'VIN not reported'}</p>
          </div>

          <div className="row wrap" style={{ gap: 8 }}>
            <span className={`pill ${result.milOn ? 'warn' : 'ok'}`}>
              <i className="dot" />
              <Term of="MIL">{result.milOn ? 'MIL on' : 'MIL off'}</Term>
            </span>
            <span className={`pill ${stored.length > 0 ? 'crit' : 'ok'}`}>
              <i className="dot" />
              {stored.length} stored
            </span>
            {result.protocol && <span className="pill">{result.protocol}</span>}
          </div>
        </div>

        {result.vin && (
          <div className="row" style={{ marginTop: 14 }}>
            <input
              type="text"
              value={label}
              placeholder="Name this vehicle, e.g. Shop truck"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
              style={{ maxWidth: 280 }}
            />
            <button onClick={saveLabel} disabled={savingLabel || label === (vehicle?.label ?? '')}>
              Save name
            </button>
          </div>
        )}

        <VehicleSpecs vehicle={vehicle} />

        <VehicleExtras vehicle={vehicle} supportedPids={result.supportedPids} />
      </div>

      <LivePanel
        vitals={result.vitals}
        live={live}
        liveKeys={liveKeys}
        samples={samples}
        liveHz={liveHz}
        onToggleLiveKey={onToggleLiveKey}
        units={units}
      />

      <RecallsPanel vehicle={vehicle} />

      <ReadinessPanel readiness={result.readiness} />

      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          {/* Clearing lives with the session controls in the sidebar now. It
              needs an open connection, which is what that panel is about. */}
          <h2 style={{ margin: 0, flex: 1 }}>Diagnostic codes</h2>
        </div>

        {codes.length === 0 ? (
          <div className="empty">
            <strong>No codes</strong>
            Nothing stored, pending or permanent. The vehicle reported a clean scan.
          </div>
        ) : (
          <>
            <div className="dtc-list">
              {codes.map((dtc) => (
                <Fragment key={dtc.code}>
                  <DtcRow
                    dtc={dtc}
                    selected={selectedCode === dtc.code}
                    onSelect={() => onSelectCode(selectedCode === dtc.code ? null : dtc.code)}
                  />
                  {result.freezeFrame?.dtc === dtc.code && (
                    <FreezeFrameCard frame={result.freezeFrame} scanVitals={result.vitals} units={units} />
                  )}
                </Fragment>
              ))}

              {/* A frame can outlive its code, so show it rather than dropping it. */}
              {result.freezeFrame && !codes.some((c) => c.code === result.freezeFrame?.dtc) && (
                <FreezeFrameCard frame={result.freezeFrame} scanVitals={result.vitals} units={units} orphaned />
              )}
            </div>
            {stored.length > 0 && (
              <p className="small dim" style={{ marginTop: 12, marginBottom: 0 }}>
                Clearing erases the freeze frame above along with the codes, and resets readiness monitors, which can
                fail an emissions test until the drive cycle completes. Read the conditions down before clearing.
              </p>
            )}
          </>
        )}
      </div>

      <MonitorTestsPanel tests={result.monitorTests} readiness={result.readiness} />
      <CalibrationPanel info={result.vehicleInfo} adapterVoltage={result.adapterVoltage} />
    </div>
  );
}

function DtcRow({ dtc, selected, onSelect }: { dtc: Dtc; selected: boolean; onSelect: () => void }) {
  return (
    <button className={`dtc sev-${dtc.severity}`} aria-selected={selected} onClick={onSelect}>
      <span className="stripe" />
      <span className="code">{dtc.code}</span>
      <span className="desc">
        <span>{dtc.description}</span>
        <span className="sys">
          {dtc.system}
          {dtc.generic && ' · range description only'}
        </span>
      </span>
      <span className="meta">
        <span className={`pill ${kindTone(dtc.kind)}`}>{dtc.kind}</span>
        <span className="dim small">{selected ? 'hide parts' : 'parts'}</span>
      </span>
    </button>
  );
}

function kindTone(kind: Dtc['kind']): string {
  return { stored: 'crit', pending: 'warn', permanent: 'info' }[kind];
}

/**
 * The snapshot the ECU took when the code set.
 *
 * Each frozen value carries the scan-time reading beside it when the two
 * differ, because the gap between them is the diagnosis: a catalyst code that
 * set at 2,400 rpm and 61% load is a different fault from the same code at
 * idle, and the numbers should say so without the technician doing arithmetic.
 */
function FreezeFrameCard({
  frame,
  scanVitals,
  units,
  orphaned = false,
}: {
  frame: FreezeFrame;
  scanVitals: Vitals;
  units: UnitSystem;
  orphaned?: boolean;
}) {
  const populated = PIDS.filter((spec) => frame.vitals[spec.key] !== null && frame.vitals[spec.key] !== undefined);
  if (populated.length === 0) return null;

  return (
    <section className="freeze">
      <header>
        <span className="eyebrow" style={{ margin: 0 }}>
          Freeze frame
        </span>
        <span className="small">
          {orphaned ? (
            <>
              Recorded when <span className="mono">{frame.dtc}</span> set. That code is no longer reported.
            </>
          ) : (
            'Conditions the ECU recorded at the moment this code set'
          )}
        </span>
      </header>

      <div className="vitals">
        {populated.map((spec) => {
          const frozen = frame.vitals[spec.key] as number;
          const now = scanVitals[spec.key] ?? null;
          const differs = now !== null && now !== frozen;
          const shown = convertVital(spec, frozen, units);
          return (
            <div className="vital freeze-vital" key={spec.key}>
              <span className="k">{spec.label}</span>
              <div className="v">
                {shown.value.toLocaleString(undefined, { maximumFractionDigits: shown.precision })}
                <span className="u">{shown.unit}</span>
              </div>
              {differs && <span className="then">at scan {formatVital(spec, now, units)}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * A single-series trend line inside a stat tile.
 *
 * One series, so no legend: the tile's own label names it. The line stays thin
 * and the baseline recessive so the number above remains the thing you read
 * first; the only emphasis is a dot on the newest sample, which is where the
 * eye needs to land when the value is moving.
 */
